import { motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { HiArrowDownRight } from 'react-icons/hi2';
import { MagneticButton } from './MagneticButton';
import { SplitWords } from './SplitWords';
import { EASE } from '../lib/motion';

const EMAIL = 'konstantinos@kouk.gr';
const VAT = 'EL161195621';

/**
 * Closing manifesto moment: the 3D core returns to centre stage with its
 * rings, and the question is set right across it.
 */
export function Contact() {
  const { t } = useTranslation('common');
  const reduce = useReducedMotion();

  const details = [
    { label: t('contact.emailLabel'), value: EMAIL, href: `mailto:${EMAIL}` },
    { label: t('contact.vatLabel'), value: VAT, href: undefined },
  ];

  const fadeUp = (delay: number) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.6 },
    transition: { duration: 0.9, ease: EASE, delay },
  });

  return (
    <section
      id="contact"
      className="relative flex min-h-[100dvh] items-center px-5 py-28 md:px-8"
    >
      <div className="mx-auto flex w-full max-w-[1400px] flex-col items-center text-center">
        <motion.p
          {...fadeUp(0)}
          className="mb-8 font-mono text-xs uppercase tracking-[0.22em] text-accent"
        >
          {t('contact.kicker')}
        </motion.p>
        <h2 className="max-w-[14ch] font-display text-[clamp(3rem,9vw,9rem)] font-semibold leading-[0.95] tracking-[-0.05em] [text-shadow:0_4px_40px_rgba(10,12,11,0.55)]">
          <SplitWords text={t('contact.title')} stagger={0.08} />
        </h2>
        <motion.p
          {...fadeUp(0.25)}
          className="mt-8 max-w-[46ch] text-lg leading-relaxed text-fg/80"
        >
          {t('contact.body')}
        </motion.p>

        <motion.div {...fadeUp(0.4)} className="mt-12">
          <MagneticButton
            href={`mailto:${EMAIL}`}
            className="px-9 py-4 text-[16px]"
          >
            {t('contact.cta')}
            <HiArrowDownRight className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
          </MagneticButton>
        </motion.div>

        <motion.dl
          {...fadeUp(0.5)}
          className="mt-20 grid gap-8 sm:grid-cols-2 sm:gap-20"
        >
          {details.map((d) => (
            <div key={d.label}>
              <dt className="text-sm text-faint">{d.label}</dt>
              <dd className="mt-1.5">
                {d.href ? (
                  <a
                    href={d.href}
                    className="bg-[linear-gradient(currentColor,currentColor)] bg-[length:0%_1px] bg-left-bottom bg-no-repeat pb-0.5 text-[17px] text-fg transition-[background-size,color] duration-500 ease-out-expo hover:bg-[length:100%_1px] hover:text-accent"
                  >
                    {d.value}
                  </a>
                ) : (
                  <span className="font-mono text-[17px] text-fg">
                    {d.value}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </motion.dl>
      </div>
    </section>
  );
}
